<template>
  <tx-dialog v-model="visible" :title="t('orderlines.actions.fill')" width="600px" @closed="onClosed">
    <div class="flex flex-col w-full h-full">
      <!-- STEPS -->
      <div class="px-6 py-4 mb-10 bg-gray-100 border-t border-gray-200">
        <div class="flex justify-between">
          <template v-for="(step, index) in steps" :key="step">
            <div class="flex flex-col items-center">
              <div
                class="flex items-center justify-center w-6 h-6 border-2 rounded-full"
                :class="[index <= currentStepIndex ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-400']"
                v-text="index + 1"
              />
              <div class="text-sm" v-text="step" />
            </div>
            <div
              v-if="index < steps.length - 1" class="flex-1 h-2 mx-1 mt-3"
              :class="[index < currentStepIndex ? 'bg-blue-500' : 'bg-gray-400']"
            />
          </template>
        </div>
      </div>

      <!-- FORM -->
      <div class="overflow-auto h-80">
        <!-- DATA STEP -->
        <div v-show="currentStepIndex === 0" class="flex flex-col">
          <span class="block mb-5">{{ t('orderlines.fillOrder.description') }}</span>
          <tx-tabs v-model="selectedTab" class="mb-5" :tabs="tabs" />

          <div
            v-for="(attribute, index) in visibleAttributes" :key="attribute.SystemName"
            :class="{ 'col-span-2': index === 0, 'col-span-1': index > 0 }" class="mb-5 last-of-type:mb-4"
          >
            <form-editor
              v-model="formModel[attribute.SystemName]"
              :attribute="attribute"
              :form="formModel"
              :required="attribute.IsRequired"
              :errors="v$[attribute.SystemName]?.$errors"
              :show-label="true"
              :clearable="true"
              :sub-section-data="attribute.SystemName === 'Favorites' ? subSectionDataForFavorites : undefined"
              @blur="v$[attribute.SystemName]?.$touch"
            />
          </div>
        </div>
        <div v-show="currentStepIndex === 1" class="flex flex-col">
          <div v-show="validArticles.length" class="p-[5px] mb-5 border border-[#76bceb] bg-[#eff3f6] rounded">
            <span class="block mb-2">{{ t('orderlines.fillOrder.validListDescription', validArticles.length) }}</span>
            <span class="block">{{ validArticles.map(x => x.ArticleNumber).join(', ') }}</span>
          </div>
          <div v-show="unavailableArticleNumbers.length" class="p-[5px] border border-[#76bceb] bg-[#eff3f6] rounded">
            <span class="block mb-2">{{ t('orderlines.fillOrder.unavailableListDescription', unavailableArticleNumbers.length) }}</span>
            <span class="block">{{ unavailableArticleNumbers.join(', ') }}</span>
          </div>
          <div v-show="!validArticles.length && !unavailableArticleNumbers.length" class="p-[5px] border border-[#76bceb] bg-[#eff3f6] rounded">
            <span class="block mb-2">{{ t('orderlines.fillOrder.noValidArticles') }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-center px-6 py-4 space-x-3 sm:justify-end bg-gray-50">
        <tx-button
          type="cancel" :loading="loading" :text="t('general.cancel')"
          width="113px" height="40px" @click="close(undefined)"
        />
        <tx-button
          v-show="currentStepIndex > 0" type="cancel" :disabled="currentStepIndex <= 0" :loading="loading" :text="t('general.back')"
          width="113px" height="40px" @click="onBack"
        />
        <tx-button
          v-show="currentStepIndex < (steps.length - 1)" type="confirm" :loading="loading" :text="t('general.next')"
          width="113px" height="40px" :disabled="v$.$invalid" @click="onNext"
        />
        <tx-button
          v-show="currentStepIndex >= (steps.length - 1)" type="confirm" :loading="loading"
          :text="t('general.fill')" width="113px" height="40px" :disabled="v$.$invalid || validArticles.length === 0" @click="onFinish"
        />
      </div>
    </template>
  </tx-dialog>
</template>

<script lang='ts' setup>
import useVuelidate from '@vuelidate/core'
import { computed, reactive, ref, watch } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import appConfig from '@/services/appConfig'
import FormEditor from '@/shared/components/FormEditor.vue'
import type FavoriteTag from '@/models/favoriteTag'
import type Order from '@/models/order'
import type Orderline from '@/models/orderline'
import TxButton from '@/shared/components/TxButton.vue'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxTabs from '@/shared/components/TxTabs.vue'
import { AttributeType } from '@/models/catalogAttribute'
import { appConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import type { Segmentation } from '@/models/customerSegmentation'
import type Article from '@/models/article'

const props = defineProps<{
  currentOrder: Order
  modelValue: boolean
  customerSegmentations: Segmentation[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: boolean): void
  (e: 'closed', createdOrderlines?: Array<Orderline>): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const visible = ref(false)
const loading = ref(false)
const formModel = reactive({
  ArticlesList: '',
  Favorites: [],
})
const selectedTab = ref<'favorites' | 'articlesList'>('favorites')
const availableFavorites = ref<Array<FavoriteTag>>([])
const tabs: ITabItem[] = [
  { key: 'favorites', label: 'orderlines.fillOrder.tabFavorites', visible: true },
  { key: 'articlesList', label: 'orderlines.fillOrder.tabArticlesList', visible: true },
]
const currentStepIndex = ref(0)
const steps = ['Data', 'Validation']
let validArticles: Array<Article> = []
let unavailableArticleNumbers: Array<string> = []
const subSectionDataForFavorites: ISubSectionData = {
  mainSectionLabel: 'My Tags',
  subSectionlabel: 'Shared Tags',
  subSectionByAttribute: { CreatedByUserName: userStore.currentUsername },
  subSectionDisplayProp: 'Tag',
  subSectionSecondaryDisplayProp: 'CreatedByUserName',
}

const visibleAttributes = computed(() => {
  const visibleAttributes: Array<IFormEditorAttribute> = []
  if (selectedTab.value === 'favorites') {
    const customersField: IMyAttribute = Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'Favorites',
      AttributeType: AttributeType.MultiValue,
      DisplayName: t('orderlines.fillOrder.favoriteTags'),
      Creatable: true,
      DropDownData: availableFavorites.value,
      DropDownValueProp: 'Id',
      DropDownValueDisplayProp: 'Tag',
      DropDownValueSecondaryDisplayProp: 'CreatedByUserName',
      IsRequired: true,
    })
    visibleAttributes.push(customersField)
  }
  else if (selectedTab.value === 'articlesList') {
    const customersField: IMyAttribute = Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'ArticlesList',
      AttributeType: AttributeType.Nvarchar,
      DisplayName: t('orderlines.fillOrder.articleListLabel'),
      Creatable: true,
      InputType: 'textarea',
      IsRequired: true,
    })
    visibleAttributes.push(customersField)
  }
  return visibleAttributes
})

const validations = computed(() => {
  const result: Record<string, any> = {}
  if (selectedTab.value === 'articlesList') {
    result.ArticlesList = {}
    result.ArticlesList.required = helpers.withMessage(t('validations.required', { property: t('orderlines.fillOrder.articleListLabel') }), required)
    delete result.Favorites
  }
  else if (selectedTab.value === 'favorites') {
    result.Favorites = {}
    result.Favorites.required = helpers.withMessage(t('validations.required', { property: t('orderlines.fillOrder.articleListLabel') }), required)
    delete result.ArticlesList
  }
  return result
})

const v$ = useVuelidate(validations, formModel)

function init() {
  loadFavorites()
}

function reset() {
  loading.value = false
  currentStepIndex.value = 0
  formModel.ArticlesList = ''
  formModel.Favorites = []
  selectedTab.value = 'favorites'
  v$.value.$reset()
}

function onBack() {
  if (currentStepIndex.value < 0) {
    return
  }
  currentStepIndex.value--
}

async function onNext() {
  if (currentStepIndex.value >= (steps.length - 1)) {
    return
  }
  if (currentStepIndex.value === 0) {
    await validateData()
  }
  currentStepIndex.value++
}

async function validateData() {
  validArticles = []
  unavailableArticleNumbers = []
  if (appConfig.DB && userStore.activeCatalog) {
    if (selectedTab.value === 'articlesList') {
      const articleNumbers = formModel.ArticlesList.split(/\r?\n/)
      validArticles = await appConfig.DB.getArticlesByCatalogCodeArticleNumbers(userStore.activeCatalog, userStore.linkedCatalogDetails, articleNumbers, userStore.currentCustomer, [], false, userStore.currentCustomerSegmentations, userStore.myAttributes!)
      const validArticlesNumbers = new Set(validArticles.map(x => x.ArticleNumber))
      articleNumbers.forEach((articleNumber) => {
        // TODO: check for article stock when we implement stock module
        if (!validArticlesNumbers.has(articleNumber)) {
          unavailableArticleNumbers.push(articleNumber)
        }
      })
    }
    else if (selectedTab.value === 'favorites') {
      try {
        const selectedFavorites = await appConfig.DB!.myFavorites.where('Id').anyOf(formModel.Favorites).toArray()
        const selectedSharedFavorites = await appConfig.DB!.sharedFavorites.where('Id').anyOf(formModel.Favorites).toArray()
        const allFavorites = [...selectedFavorites, ...selectedSharedFavorites]
        const articleIds = new Set<number>()
        allFavorites.forEach((tag) => {
          tag.Articles.forEach(articleId => articleIds.add(articleId))
        })
        validArticles = await appConfig.DB.getArticlesByCatalogCodeArticleIds(userStore.activeCatalog, userStore.linkedCatalogDetails, [...articleIds], userStore.currentCustomer, userStore.myAttributes!, [], false, userStore.currentCustomerSegmentations, false)
      }
      catch (error) {
        console.error(error)
      }
    }
  }
}

async function loadFavorites() {
  try {
    const myFavorites = (await appConfig.DB!.myFavorites
      .where({ CatalogCode: userStore.activeCatalog!.CatalogCode, Status: 1, UserId: userStore.userProfile.Id, CustomerId: userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1 })
      .filter((tag) => {
        if (tag.CreatedByUserName.toLowerCase() === userStore.currentUsername.toLowerCase()) { return true }
        return false
      })
      .toArray()
    ).sort((a, b) => {
      if (a.CreatedByUserName.toLowerCase() === userStore.currentUsername.toLowerCase()) { return -1 }
      return a.Tag.localeCompare(b.Tag)
    })

    const sharedFavorites = (await appConfig.DB!.sharedFavorites
      .where({ CatalogCode: userStore.activeCatalog!.CatalogCode, UserId: userStore.userProfile.Id })
      .toArray()
    ).sort((a, b) => {
      return a.Tag.localeCompare(b.Tag)
    })
    availableFavorites.value = [...myFavorites, ...sharedFavorites]
  }
  catch (error) {
    availableFavorites.value = []
    console.warn(error)
  }
}

function fillOrder(articles: Array<Article>) {
  // no need to fill the crd object, it will be taken care when adding quantities
  const createdOrderlines = props.currentOrder.fillOrder(articles)
  close(createdOrderlines)
}

function onFinish() {
  fillOrder(validArticles)
}

function onClosed() {
  emit('closed')
}

function close(createdOrderlines?: Array<Orderline>) {
  visible.value = false
  emit('closed', createdOrderlines)
}

watch(() => props.modelValue, (modelValue) => {
  visible.value = modelValue
  if (modelValue) {
    init()
  }
  else {
    reset()
  }
})
</script>
